'use client'

import React, { useState, useMemo } from 'react'
import Image, { ImageProps } from 'next/image'
import { cn } from '@/hooks/utils'
import { useEnv } from '@/components/providers/EnvProvider'

interface ImageComponentProps extends ImageProps {
    containerClassName?: string
    showLoader?: boolean
    disableAnimation?: boolean
}

/**
 * A reusable Image component that handles lazy loading with a placeholder (skeleton/loader).
 * It uses next/image for performance and optimization.
 */
export function ImageComponent({
    src,
    alt,
    className,
    containerClassName,
    showLoader = true,
    disableAnimation = false,
    onLoad,
    quality,
    ...props
}: ImageComponentProps) {
    const { ASSETS_URL } = useEnv()
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(!src)

    const refinedSrc = useMemo(() => {
        if (!src || typeof src !== 'string') return src
        if (src.startsWith('http') || src.startsWith('/') || src.startsWith('data:')) {
            return src
        }
        return `${ASSETS_URL}${src}`
    }, [src, ASSETS_URL])

    React.useEffect(() => {
        setHasError(!refinedSrc)
        if (refinedSrc) {
            setIsLoading(true)
        }
    }, [refinedSrc])

    const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setIsLoading(false)
        if (onLoad) {
            onLoad(event)
        }
    }

    const handleError = () => {
        setIsLoading(false)
        setHasError(true)
    }

    return (
        <div
            className={cn(
                'relative w-full',
                !containerClassName?.includes('h-') && 'h-full',
                !containerClassName?.includes('overflow-') && 'overflow-hidden',
                containerClassName,
            )}
        >
            {/* Loading Placeholder */}
            {isLoading && !disableAnimation && (
                <div className="absolute inset-0 flex animate-pulse items-center justify-center">
                    {showLoader && (
                        <div className="size-8 animate-spin rounded-full border-2 border-[#7C38F6]/30 border-t-[#7C38F6]" />
                    )}
                </div>
            )}

            {/* Error Fallback */}
            {hasError && (
                <div
                    style={{
                        background: `linear-gradient(180deg, #E5E5E5 0%, #999 100%)`
                    }}
                    className="flex h-full w-full flex-col items-center justify-center  p-4 text-center text-gray-500">
                    <svg
                        className="mb-2 h-8 w-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                    <p className="text-sm text-gray-400">Failed to load</p>
                </div>
            )}

            {/* Target Image */}
            {!hasError && (
                <Image
                    src={refinedSrc}
                    alt={alt}
                    className={cn(
                        !disableAnimation && 'transition-all duration-200 ease-in-out',
                        !disableAnimation && (isLoading ? 'opacity-0 blur-lg' : 'opacity-100 blur-0'),
                        className,
                    )}
                    onLoad={handleLoad}
                    onError={handleError}
                    {...props}
                />
            )}
        </div>
    )
}

